// Operator Context for Central de Monitoreo
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OperatorContext = createContext(null);

// Create axios instance for operator
const operatorApi = axios.create({
  baseURL: API_BASE,
  headers: { 'Content-Type': 'application/json' },
});

// Add auth interceptor
operatorApi.interceptors.request.use((config) => {
  const token = localStorage.getItem('mercapoli_operator_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
operatorApi.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('mercapoli_operator_token');
      localStorage.removeItem('mercapoli_operator');
      window.location.href = '/operator-login';
    }
    return Promise.reject(error);
  }
);

export const OperatorProvider = ({ children }) => {
  const [operator, setOperator] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [emergencies, setEmergencies] = useState([]);
  const [activeServices, setActiveServices] = useState([]);
  const [supportTickets, setSupportTickets] = useState([]);
  const [featureFlags, setFeatureFlags] = useState({});

  // Check authentication on mount
  useEffect(() => {
    const token = localStorage.getItem('mercapoli_operator_token');
    const storedOperator = localStorage.getItem('mercapoli_operator');
    
    if (token && storedOperator) {
      try {
        setOperator(JSON.parse(storedOperator));
        setIsAuthenticated(true);
      } catch {
        logout();
      }
    }
    setLoading(false);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('mercapoli_operator_token');
    localStorage.removeItem('mercapoli_operator');
    setOperator(null);
    setIsAuthenticated(false);
    window.location.href = '/operator-login';
  }, []);

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    try {
      const res = await operatorApi.get('/operator/dashboard');
      setDashboard(res.data);
      setFeatureFlags(res.data.feature_flags || {});
      return res.data;
    } catch (error) {
      console.error('Error loading dashboard:', error);
      throw error;
    }
  }, []);

  // Load emergencies
  const loadEmergencies = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.severity) params.append('severity', filters.severity);
      if (filters.country_code) params.append('country_code', filters.country_code);
      
      const res = await operatorApi.get(`/operator/emergencies?${params.toString()}`);
      setEmergencies(res.data);
      return res.data;
    } catch (error) {
      console.error('Error loading emergencies:', error);
      throw error;
    }
  }, []);

  // Get emergency detail
  const getEmergencyDetail = useCallback(async (alertId) => {
    try {
      const res = await operatorApi.get(`/operator/emergency/${alertId}`);
      return res.data;
    } catch (error) {
      console.error('Error loading emergency detail:', error);
      throw error;
    }
  }, []);

  // Take emergency
  const takeEmergency = useCallback(async (alertId) => {
    try {
      const res = await operatorApi.post(`/operator/emergency/${alertId}/take`);
      await loadEmergencies();
      return res.data;
    } catch (error) {
      console.error('Error taking emergency:', error);
      throw error;
    }
  }, [loadEmergencies]);

  // Load active services
  const loadActiveServices = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.service_type) params.append('service_type', filters.service_type);
      if (filters.country_code) params.append('country_code', filters.country_code);
      
      const res = await operatorApi.get(`/operator/services/active?${params.toString()}`);
      setActiveServices(res.data);
      return res.data;
    } catch (error) {
      console.error('Error loading active services:', error);
      throw error;
    }
  }, []);

  // Load support tickets
  const loadSupportTickets = useCallback(async (filters = {}) => {
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.category) params.append('category', filters.category);
      
      const res = await operatorApi.get(`/operator/support/tickets?${params.toString()}`);
      setSupportTickets(res.data);
      return res.data;
    } catch (error) {
      console.error('Error loading support tickets:', error);
      throw error;
    }
  }, []);

  // Take support ticket
  const takeSupportTicket = useCallback(async (ticketId) => {
    try {
      const res = await operatorApi.post(`/operator/support/ticket/${ticketId}/take`);
      await loadSupportTickets();
      return res.data;
    } catch (error) {
      console.error('Error taking ticket:', error);
      throw error;
    }
  }, [loadSupportTickets]);

  // Reply to support ticket
  const replyToTicket = useCallback(async (ticketId, message) => {
    try {
      const res = await operatorApi.post(`/operator/support/ticket/${ticketId}/reply`, null, {
        params: { message }
      });
      return res.data;
    } catch (error) {
      console.error('Error replying to ticket:', error);
      throw error;
    }
  }, []);

  // Resolve support ticket
  const resolveTicket = useCallback(async (ticketId, resolutionNotes) => {
    try {
      const res = await operatorApi.post(`/operator/support/ticket/${ticketId}/resolve`, null, {
        params: { resolution_notes: resolutionNotes }
      });
      await loadSupportTickets();
      return res.data;
    } catch (error) {
      console.error('Error resolving ticket:', error);
      throw error;
    }
  }, [loadSupportTickets]);

  // Get WhatsApp link
  const getWhatsAppLink = useCallback(async (userId, context = 'general', alertId = null) => {
    try {
      const params = new URLSearchParams({ context });
      if (alertId) params.append('alert_id', alertId);
      
      const res = await operatorApi.get(`/operator/whatsapp-link/${userId}?${params.toString()}`);
      return res.data;
    } catch (error) {
      console.error('Error getting WhatsApp link:', error);
      throw error;
    }
  }, []);

  // Get risk score
  const getRiskScore = useCallback(async (userId) => {
    try {
      const res = await operatorApi.get(`/risk-score/${userId}`);
      return res.data;
    } catch (error) {
      console.error('Error getting risk score:', error);
      throw error;
    }
  }, []);

  // Chat functions
  const getChatMessages = useCallback(async (alertId) => {
    try {
      const res = await operatorApi.get(`/chat/${alertId}/messages`);
      return res.data;
    } catch (error) {
      console.error('Error getting chat messages:', error);
      throw error;
    }
  }, []);

  const sendChatMessage = useCallback(async (alertId, content) => {
    try {
      const res = await operatorApi.post(`/chat/${alertId}/message`, { content });
      return res.data;
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  }, []);

  // Emergency admin actions (using existing API)
  const acknowledgeEmergency = useCallback(async (alertId) => {
    try {
      const res = await operatorApi.post(`/admin/emergency-alerts/${alertId}/acknowledge`);
      await loadEmergencies();
      return res.data;
    } catch (error) {
      console.error('Error acknowledging emergency:', error);
      throw error;
    }
  }, [loadEmergencies]);

  const addEmergencyNote = useCallback(async (alertId, note) => {
    try {
      const res = await operatorApi.post(`/admin/emergency-alerts/${alertId}/note`, { note });
      return res.data;
    } catch (error) {
      console.error('Error adding note:', error);
      throw error;
    }
  }, []);

  const escalateEmergency = useCallback(async (alertId) => {
    try {
      const res = await operatorApi.post(`/admin/emergency-alerts/${alertId}/escalate`);
      await loadEmergencies();
      return res.data;
    } catch (error) {
      console.error('Error escalating emergency:', error);
      throw error;
    }
  }, [loadEmergencies]);

  const resolveEmergency = useCallback(async (alertId, resolutionNotes, resolutionType = 'resolved') => {
    try {
      const res = await operatorApi.post(`/admin/emergency-alerts/${alertId}/resolve`, {
        resolution_notes: resolutionNotes,
        resolution_type: resolutionType
      });
      await loadEmergencies();
      return res.data;
    } catch (error) {
      console.error('Error resolving emergency:', error);
      throw error;
    }
  }, [loadEmergencies]);

  return (
    <OperatorContext.Provider value={{
      operator,
      isAuthenticated,
      loading,
      logout,
      // Dashboard
      dashboard,
      loadDashboard,
      featureFlags,
      // Emergencies
      emergencies,
      loadEmergencies,
      getEmergencyDetail,
      takeEmergency,
      acknowledgeEmergency,
      addEmergencyNote,
      escalateEmergency,
      resolveEmergency,
      // Services
      activeServices,
      loadActiveServices,
      // Support
      supportTickets,
      loadSupportTickets,
      takeSupportTicket,
      replyToTicket,
      resolveTicket,
      // Utils
      getWhatsAppLink,
      getRiskScore,
      // Chat
      getChatMessages,
      sendChatMessage,
    }}>
      {children}
    </OperatorContext.Provider>
  );
};

export const useOperator = () => {
  const context = useContext(OperatorContext);
  if (!context) {
    throw new Error('useOperator must be used within an OperatorProvider');
  }
  return context;
};

export default OperatorContext;
